import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Platform,
  ActivityIndicator,
  ScrollView,
  View,
  Pressable,
  KeyboardAvoidingView,
  StyleSheet,
} from 'react-native';
import { Text } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ADMIN_COLORS, ADMIN_FONT } from '../../../config/theme/adminColors';
import { SegmentedControl, useModalWidth } from '../Products/formStyles';
import {
  loadCatalogVitrineSettings,
  saveCatalogVitrineSettings,
} from '../Features/service/catalog-vitrine-settings-service';
import {
  DEFAULT_CATALOG_VITRINE_SETTINGS,
  CATALOG_SETTINGS_DOC,
  CUSTOMIZATION,
} from '../../../constants/catalogVitrineSettings';
import { pickCatalogVitrineFields } from '../../../utils/resolveCatalogVitrineSettings';
import { queryClient } from '../../../queries/queryClient';
import { useRemoteConfigContext } from '../../../contexts/useRemoteConfigContext';

const SETTINGS = 'settings';

const SUBTITLE_WITH_VITRINE =
  'Com a Vitrine de Produtos ativa: as configurações abaixo definem se o aluno visualiza apenas os produtos e módulos de conteúdo aos quais tem acesso ou se também visualiza os que estão bloqueados.';

const SUBTITLE_WITHOUT_VITRINE =
  'Sem a Vitrine de Produtos ativa: as configurações definem se o aluno visualiza apenas os módulos de conteúdo aos quais tem acesso ou se também visualiza os módulos bloqueados.';

function getCatalogVisibilityCopy(hasProductCatalog) {
  if (hasProductCatalog) {
    return {
      ownAccessDescription: 'Oculta produtos e módulos que o aluno ainda não possui.',
      showAllDescriptionWeb:
        'O aluno vê produtos e módulos bloqueados. Ao clicar, é direcionado direto ao checkout de compra.',
      showAllDescriptionMobile: 'O aluno vê produtos e módulos bloqueados.',
    };
  }

  return {
    ownAccessDescription: 'Oculta módulos que o aluno ainda não possui.',
    showAllDescriptionWeb:
      'O aluno vê módulos bloqueados. Ao clicar, é direcionado direto ao checkout de compra.',
    showAllDescriptionMobile: 'O aluno vê módulos bloqueados.',
  };
}

export default function AdminCatalogSettingsModal({ visible, onClose, onSaved }) {
  const modalWidth = useModalWidth();
  const { hasProductCatalog } = useRemoteConfigContext();
  const copy = useMemo(
    () => getCatalogVisibilityCopy(hasProductCatalog),
    [hasProductCatalog]
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_CATALOG_VITRINE_SETTINGS);
  const [initialSettings, setInitialSettings] = useState(DEFAULT_CATALOG_VITRINE_SETTINGS);
  const [fromLegacyFilters, setFromLegacyFilters] = useState(false);

  useEffect(() => {
    if (!visible) return;

    (async () => {
      setLoading(true);
      try {
        const catalog = await loadCatalogVitrineSettings();
        const fields = pickCatalogVitrineFields(catalog);
        setSettings(fields);
        setInitialSettings(fields);
        setFromLegacyFilters(!!catalog.fromLegacyFilters);
      } catch (error) {
        console.error('Erro ao carregar visibilidade do catálogo:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [visible]);

  const hasChanges = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(initialSettings),
    [settings, initialSettings]
  );

  const handleCancel = () => {
    setSettings(initialSettings);
    onClose();
  };

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await saveCatalogVitrineSettings(settings);
      setInitialSettings({ ...settings });
      queryClient.invalidateQueries({
        queryKey: [CUSTOMIZATION, CATALOG_SETTINGS_DOC],
      });
      queryClient.invalidateQueries({
        queryKey: [CUSTOMIZATION, CATALOG_SETTINGS_DOC, SETTINGS, 'legacy'],
      });
      if (typeof onSaved === 'function') {
        await onSaved();
      }
      onClose();
    } catch (error) {
      console.error('Erro ao salvar visibilidade do catálogo:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleCancel}>
      <View style={styles.overlay}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={[styles.card, { width: modalWidth, maxWidth: 640 }]}
        >
          <View style={styles.header}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.title}>Visibilidade do catálogo</Text>
              <Text style={styles.subtitle}>{SUBTITLE_WITH_VITRINE}</Text>
              <Text style={[styles.subtitle, styles.subtitleSpacing]}>
                {SUBTITLE_WITHOUT_VITRINE}
              </Text>
            </View>
            <Pressable
              onPress={handleCancel}
              hitSlop={8}
              style={({ hovered }) => [
                styles.closeBtn,
                hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
              ]}
            >
              <Ionicons name="close" size={20} color={ADMIN_COLORS.muted} />
            </Pressable>
          </View>

          <ScrollView
            style={{ maxHeight: '62vh' }}
            contentContainerStyle={{ padding: 20 }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
          >
            {loading ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator size="large" color={ADMIN_COLORS.accent} />
              </View>
            ) : (
              <>
                {fromLegacyFilters && (
                  <View style={styles.legacyBanner}>
                    <Ionicons
                      name="information-circle-outline"
                      size={18}
                      color={ADMIN_COLORS.accent}
                    />
                    <Text style={styles.legacyBannerText}>
                      Os valores abaixo refletem a configuração antiga de visibilidade
                      dos Filtros. Salve aqui para passar a usar as novas opções de
                      web e app separadamente.
                    </Text>
                  </View>
                )}

                <Text style={styles.sectionTitle}>Aplicativo Web</Text>
                <SegmentedControl
                  value={settings.restrictToUserProductsWeb ? 'own' : 'all'}
                  onChange={(value) =>
                    setSettings((current) => ({
                      ...current,
                      restrictToUserProductsWeb: value === 'own',
                    }))
                  }
                  options={[
                    {
                      value: 'all',
                      label: 'Mostrar tudo',
                      description: copy.showAllDescriptionWeb,
                      icon: 'earth-outline',
                    },
                    {
                      value: 'own',
                      label: 'Só com acesso',
                      description: copy.ownAccessDescription,
                      icon: 'lock-closed-outline',
                    },
                  ]}
                />

                <Text style={[styles.sectionTitle, { marginTop: 24 }]}>
                  Aplicativo (mobile)
                </Text>
                <SegmentedControl
                  value={settings.restrictToUserProductsMobile ? 'own' : 'all'}
                  onChange={(value) =>
                    setSettings((current) => ({
                      ...current,
                      restrictToUserProductsMobile: value === 'own',
                    }))
                  }
                  options={[
                    {
                      value: 'own',
                      label: 'Só com acesso',
                      description: copy.ownAccessDescription,
                      icon: 'lock-closed-outline',
                    },
                    {
                      value: 'all',
                      label: 'Mostrar tudo',
                      description: copy.showAllDescriptionMobile,
                      icon: 'earth-outline',
                    },
                  ]}
                />
              </>
            )}
          </ScrollView>

          <View style={styles.footer}>
            <Pressable
              onPress={handleCancel}
              style={({ hovered }) => [
                styles.btn,
                styles.btnSecondary,
                hovered && { backgroundColor: ADMIN_COLORS.border },
              ]}
            >
              <Text style={[styles.btnText, { color: ADMIN_COLORS.ink }]}>Cancelar</Text>
            </Pressable>
            <Pressable
              onPress={handleSave}
              disabled={saving || loading}
              style={({ hovered }) => [
                styles.btn,
                styles.btnPrimary,
                (saving || loading || !hasChanges) && { opacity: 0.55 },
                hovered && hasChanges && !saving && { backgroundColor: ADMIN_COLORS.accentDark },
              ]}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={[styles.btnText, { color: '#FFFFFF' }]}>Salvar</Text>
              )}
            </Pressable>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    maxHeight: '90vh',
    boxShadow: '0 20px 40px rgba(15, 23, 42, 0.18)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.border,
  },
  title: {
    fontFamily: ADMIN_FONT,
    fontSize: 18,
    fontWeight: '700',
    color: ADMIN_COLORS.ink,
  },
  subtitle: {
    marginTop: 4,
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    lineHeight: 18,
  },
  subtitleSpacing: {
    marginTop: 8,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  loadingBox: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  sectionTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.ink,
    marginBottom: 8,
  },
  legacyBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 12,
    marginBottom: 20,
    borderRadius: 10,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
  },
  legacyBannerText: {
    flex: 1,
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    lineHeight: 18,
    color: ADMIN_COLORS.muted,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  btn: {
    minHeight: 44,
    borderRadius: 10,
    paddingHorizontal: 22,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  btnPrimary: { backgroundColor: ADMIN_COLORS.accent },
  btnSecondary: { backgroundColor: ADMIN_COLORS.secondaryBg },
  btnText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600' },
});
